import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, UploadSimple, Image, X } from '@phosphor-icons/react';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBCATEGORIES = ['tops', 'bottoms', 'outerwear'];
const SIZE_OPTIONS = {
  clothing: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  pants: ['28', '30', '32', '34', '36', '38'],
};

export const AdminProductFormPage = () => {
  const { productId } = useParams();
  const isEditing = !!productId;
  const navigate = useNavigate();
  const { token, isAuthenticated, logout } = useAdmin();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    subcategory: 'tops',
    sizes: [],
    image_url: '',
    featured: false,
  });
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }

    if (isEditing) {
      fetchProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isEditing, productId, navigate]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      const product = response.data;
      setFormData({
        name: product.name,
        price: product.price.toString(),
        description: product.description,
        subcategory: product.subcategory,
        sizes: product.sizes,
        image_url: product.image_url,
        featured: product.featured,
      });
      setImagePreview(product.image_url);
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/admin/products');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'image_url') {
      setImagePreview(value);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = `${API}/images/${response.data.id}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      setImagePreview(imageUrl);
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleSize = (size) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.price || !formData.subcategory || formData.sizes.length === 0 || !formData.image_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name.toUpperCase(),
        price: parseFloat(formData.price),
        description: formData.description,
        category: 'men',
        subcategory: formData.subcategory,
        sizes: formData.sizes,
        image_url: formData.image_url,
        featured: formData.featured,
      };

      if (isEditing) {
        await axios.put(`${API}/admin/products/${productId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created');
      }

      navigate('/admin/products');
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/admin');
      }
      toast.error('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const sizeOptions = formData.subcategory === 'bottoms' ? SIZE_OPTIONS.pants : SIZE_OPTIONS.clothing;

  return (
    <div className="min-h-screen" data-testid="admin-product-form-page">
      {/* Header */}
      <div className="px-6 md:px-12 py-4 border-b border-border">
        <Link 
          to="/admin/products"
          className="text-xs uppercase tracking-wide text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
        >
          <ArrowLeft size={14} />
          BACK TO PRODUCTS
        </Link>
      </div>

      <div className="px-6 md:px-12 py-8 max-w-2xl">
        <h1 
          className="text-3xl uppercase tracking-tight font-bold mb-8"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          {isEditing ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Product Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g. URBAN COMBAT JACKET"
              className="h-12 bg-transparent border-border uppercase"
              data-testid="product-name-input"
            />
          </div>

          {/* Price */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Price (USD) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => handleChange('price', e.target.value)}
              placeholder="99.00"
              className="h-12 bg-transparent border-border"
              data-testid="product-price-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Product description..."
              className="min-h-[100px] bg-transparent border-border"
              data-testid="product-description-input"
            />
          </div>

          {/* Subcategory */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Category *</Label>
            <Select value={formData.subcategory} onValueChange={(v) => handleChange('subcategory', v)}>
              <SelectTrigger className="h-12 bg-transparent border-border" data-testid="product-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-black border-border">
                {SUBCATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat} className="uppercase">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sizes */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Available Sizes *</Label>
            <div className="flex flex-wrap gap-2">
              {sizeOptions.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => toggleSize(size)}
                  className={`px-4 py-2 border transition-colors ${
                    formData.sizes.includes(size)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:border-primary'
                  }`}
                  data-testid={`size-toggle-${size}`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Product Image *</Label>
            
            {/* Upload Button */}
            <div className="flex gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="image-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="h-12 px-6 border-border hover:bg-muted"
                data-testid="upload-image-btn"
              >
                <UploadSimple size={18} className="mr-2" />
                {uploading ? 'UPLOADING...' : 'UPLOAD IMAGE'}
              </Button>
              <span className="text-muted-foreground text-sm self-center">or</span>
            </div>

            {/* URL Input */}
            <Input
              value={formData.image_url}
              onChange={(e) => handleChange('image_url', e.target.value)}
              placeholder="Paste image URL (https://...)"
              className="h-12 bg-transparent border-border"
              data-testid="product-image-input"
            />

            {/* Image Preview */}
            {imagePreview && (
              <div className="relative mt-4 w-40 h-52 border border-border overflow-hidden group">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute top-2 right-2 p-1 bg-black/70 hover:bg-destructive transition-colors opacity-0 group-hover:opacity-100"
                  data-testid="clear-image-btn"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {!imagePreview && (
              <div className="mt-4 w-40 h-52 border border-dashed border-border flex items-center justify-center">
                <Image size={32} className="text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Featured */}
          <div className="flex items-center gap-3">
            <Checkbox
              id="featured"
              checked={formData.featured}
              onCheckedChange={(checked) => handleChange('featured', checked)}
              className="border-border data-[state=checked]:bg-primary"
              data-testid="product-featured-checkbox"
            />
            <Label htmlFor="featured" className="text-sm cursor-pointer">
              FEATURED PRODUCT (shows on homepage)
            </Label>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="bg-white text-black hover:bg-primary hover:text-white transition-colors h-12 px-8 font-bold uppercase tracking-wide"
              data-testid="save-product-btn"
            >
              {loading ? 'SAVING...' : (isEditing ? 'UPDATE PRODUCT' : 'ADD PRODUCT')}
            </Button>
            <Link to="/admin/products">
              <Button
                type="button"
                variant="outline"
                className="h-12 px-8 border-border hover:bg-muted"
              >
                CANCEL
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};
